// Opt-in web push (ADR 0077). The browser's own "Allow notifications" prompt only ever comes from a tap (iOS rejects it
// otherwise, Chrome quiets it): a banner on open (same rules as the install banner) and the button on /me ask for it,
// then subscribe this device. The texts for the service worker come from the page, in the user's language.
import { autosaveForm } from "./dom";
import { postJsonResponse } from "./http";

const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const standalone = () => matchMedia("(display-mode: standalone)").matches || (navigator as any).standalone === true;
const ios = /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
/** iPhone and iPad allow notifications only in the installed app (iOS 16.4+). */
const canAsk = () => supported() && (!ios || standalone());

const DISMISS_KEY = "pulsar-notify-dismissed", QUIET_MS = 3 * 86_400_000;
const dismissedRecently = () => { try { return Date.now() - Number(localStorage.getItem(DISMISS_KEY) ?? 0) < QUIET_MS; } catch { return false; } };
const installShownThisSession = () => { try { return sessionStorage.getItem("pulsar-install-shown") === "1"; } catch { return false; } };

const urlKey = (base64: string) => {
  const s = atob((base64 + "=".repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(s, (c) => c.charCodeAt(0));
};

/** Hand the notification texts (in this user's language) to the service worker. */
async function sendStrings() {
  const strings = document.getElementById("notify")?.dataset.strings;
  if (!strings || !supported()) return;
  const reg = await navigator.serviceWorker.ready;
  reg.active?.postMessage({ type: "push-strings", strings: JSON.parse(strings) });
}

/** From a tap: ask, subscribe this device, save it. `enableAll`: turn on every kind (the banner's promise). */
export async function turnOn(enableAll: boolean): Promise<NotificationPermission> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission;
  const reg = await navigator.serviceWorker.ready;
  const { publicKey } = await (await fetch("/api/push/key")).json();
  const sub = (await reg.pushManager.getSubscription()) ?? await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlKey(publicKey) });
  const saved = await postJsonResponse("/api/push/subscribe",
    { subscription: sub.toJSON(), enableAll, label: navigator.userAgent.slice(0, 80), tz: Intl.DateTimeFormat().resolvedOptions().timeZone });
  if (!saved.ok) throw new Error(`the server refused the device (${saved.status}${await saved.json().then((b) => ": " + b.error, () => "")})`); // the browser's subscription alone delivers nothing
  await sendStrings();
  return permission;
}

// the banner on open: signed in, still undecided, can ask here, not right after the install banner, not every day,
// not on pages that wait for something or during a lesson
const banner = document.getElementById("notify");
if (banner && canAsk() && Notification.permission === "default" && !dismissedRecently() && !installShownThisSession()
  && !banner.dataset.quiet && !document.querySelector("[data-keep]")) {
  const hide = () => banner.classList.remove("show");
  banner.querySelector<HTMLButtonElement>(".x")!.onclick = () => { try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {} hide(); };
  banner.querySelector<HTMLButtonElement>(".go")!.onclick = async () => {
    const result = await turnOn(true).catch(() => "default" as NotificationPermission);
    if (result !== "default") hide();
    if (result === "granted" && location.pathname === "/profile/notifications") location.reload(); // the switches show what was turned on
  };
  // after the install banner has had its turn (it shows at 1.5 s): never both in one session, installing comes first
  setTimeout(() => {
    if (installShownThisSession() || document.getElementById("install")?.classList.contains("show")) return;
    banner.classList.add("show");
  }, 2500);
}

// the card on /me: the right sentence or the button for this device
const card = document.getElementById("notifications");
if (card) {
  const state = card.querySelector<HTMLElement>("#push-state")!, button = card.querySelector<HTMLButtonElement>("#push-on")!, test = card.querySelector<HTMLButtonElement>("#push-test")!;
  const tz = card.querySelector<HTMLInputElement>("#push-tz");
  if (tz) tz.value = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const say = (text?: string, detail?: string) => { state.textContent = [text, detail].filter(Boolean).join(" "); state.hidden = !text; };
  const turnOnLabel = button.textContent;
  // the push service this browser's subscription points at (no secrets in the host), to tell devices apart when troubleshooting
  const service = (endpoint: string) => { try { return new URL(endpoint).host; } catch { return "?"; } };
  const show = async () => {
    if (!supported()) return say(card.dataset.unsupported);
    if (ios && !standalone()) return say(card.dataset.ios);
    if (Notification.permission === "denied") return say(card.dataset.denied);
    const sub = Notification.permission === "granted" ? await (await navigator.serviceWorker.ready).pushManager.getSubscription() : null;
    if (sub) {
      // the browser's subscription alone proves nothing: ask the server whether this device is one of the user's
      const registered = await postJsonResponse("/api/push/registered", { endpoint: sub.endpoint }).then((r) => r.ok ? r.json() : null).then((b) => b ? !!b.registered : null).catch(() => null);
      if (registered === null) say(card.dataset.checkFailed, `(${service(sub.endpoint)})`);
      else if (registered) say(card.dataset.on);
      else say(card.dataset.unregistered, `(${service(sub.endpoint)})`);
      button.textContent = registered === false ? card.dataset.register! : turnOnLabel;
      button.hidden = registered !== false; test.hidden = registered !== true;
      return;
    }
    say(); button.textContent = turnOnLabel; button.hidden = false; test.hidden = true;
  };
  button.onclick = async () => {
    button.disabled = true;
    const failed = await turnOn(false).then(() => null, (e) => String(e?.message ?? e));
    button.disabled = false;
    await show();
    if (failed) say(card.dataset.registerFailed, `(${failed})`); // the reason, for a bug report
  };
  // a real push through the server and the browser's push service, so it proves the whole way works
  test.onclick = async () => {
    test.disabled = true;
    const sub = await (await navigator.serviceWorker.ready).pushManager.getSubscription();
    const ok = sub ? (await postJsonResponse("/api/push/test", { endpoint: sub.endpoint }).then((r) => r.ok ? r.json() : { ok: false }).catch(() => ({ ok: false }))).ok : false;
    say(ok ? card.dataset.testSent : card.dataset.testFailed);
    test.disabled = false;
    if (!ok) setTimeout(show, 6000);
  };
  void show();
}

// keep the service worker's texts in the current language
if (supported() && Notification.permission === "granted") void sendStrings();

// every settings form that saves on change (AutosaveForm: General, Quick start, chess, notifications, board and pieces),
// without a page load; a new language reloads the page in it
for (const form of document.querySelectorAll<HTMLFormElement>("form[data-autosave]")) {
  autosaveForm(form, form.querySelector(".save-failed"), (changed) => { if ((changed as HTMLInputElement).name === "lang") location.reload(); });
}
